import { useEffect, useRef, useState } from 'react';
import { Check, ChevronRight, ImagePlus, ListChecks, Trash2 } from 'lucide-react';
import clsx from 'clsx';
import { businessType, FAMILIES, MODULES, type ModuleArea, planAllows, resolveModules } from '@makila/pos-core';
import { api, patch } from './api';
import { useBusiness } from './business';
import { BusinessPicker, type BusinessChoice, choicePayload, emptyChoice, TYPE_ICON } from './onboarding/BusinessPicker';
import { PROVINCES, VAT_REGIMES } from './onboarding/Signup';
import { Alert, Badge, Button, Card, Dialog, Field, FormActions, Input, PhoneInput, Section, Select, Skeleton, Textarea, Toggle, useToast } from './ui';
import { attempt, useLoad } from './useLoad';
import { navigate } from './navigate';

interface CompanyData {
  company: { legalName: string; tradeName: string | null; nuit: string; vatRegime: string; address: string; city: string; province: string | null; phone: string | null; email: string | null; logo: string | null; receiptFooter: string | null };
  business: { type: string; plan: string; overrides: Record<string, boolean>; modules: string[] };
}

/** Logótipo para o talão: quadrado, até 240 px, PNG (o talão é a preto e branco). */
async function logoThumb(file: File): Promise<string> {
  const bmp = await createImageBitmap(file);
  const scale = Math.min(1, 240 / Math.max(bmp.width, bmp.height));
  const c = document.createElement('canvas');
  c.width = Math.round(bmp.width * scale); c.height = Math.round(bmp.height * scale);
  c.getContext('2d')!.drawImage(bmp, 0, 0, c.width, c.height);
  const url = c.toDataURL('image/png');
  if (url.length > 58_000) throw new Error('Logótipo demasiado detalhado: use uma versão mais simples.');
  return url;
}

/** Definições → Empresa: o que vai no talão. A firma e o NUIT são a identidade fiscal e não mudam aqui. */
export function CompanyPage() {
  const { data, error, reload } = useLoad<CompanyData>('/settings/company');
  const [f, setF] = useState<CompanyData['company'] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const file = useRef<HTMLInputElement>(null);
  useEffect(() => { if (data) setF({ ...data.company }); }, [data]);
  if (error) return <Alert>{error}</Alert>;
  if (!data || !f) return <Card><Skeleton rows={6} /></Card>;
  const dirty = JSON.stringify(f) !== JSON.stringify(data.company);

  const save = async () => {
    setBusy(true);
    const e = await attempt(() => patch('/settings/company', {
      tradeName: f.tradeName ?? '', address: f.address, city: f.city, province: f.province ?? '', phone: f.phone || null, email: f.email ?? '', logo: f.logo, receiptFooter: f.receiptFooter ?? '',
    }));
    setBusy(false);
    setErr(e);
    if (!e) { toast('Dados da empresa gravados'); void reload(); }
  };

  return (
    <div className="max-w-4xl">
      <Card>
        <div className="space-y-8">
          <Section title="Identidade fiscal" description="Vem do registo e sai em todos os documentos. Para a mudar, fale com o suporte.">
            <Field label="Firma"><Input disabled value={f.legalName} /></Field>
            <Field label="NUIT"><Input disabled value={f.nuit} className="num w-40" /></Field>
            <Field label="Regime de IVA" hint="Muda-se com o suporte (afecta os documentos)"><Input disabled value={VAT_REGIMES.find((v) => v.value === f.vatRegime)?.label ?? f.vatRegime} /></Field>
          </Section>
          <Section title="Nome e contactos" description="Saem no talão e nas mensagens aos clientes.">
            <Field label="Nome comercial" hint="O nome que os clientes conhecem"><Input value={f.tradeName ?? ''} onChange={(e) => setF({ ...f, tradeName: e.target.value })} placeholder={f.legalName} /></Field>
            <Field label="Telefone"><PhoneInput value={f.phone ?? ''} onChange={(v) => setF({ ...f, phone: v })} /></Field>
            <Field label="Email"><Input type="email" value={f.email ?? ''} onChange={(e) => setF({ ...f, email: e.target.value })} /></Field>
          </Section>
          <Section title="Morada">
            <Field label="Província">
              <Select value={f.province ?? ''} onChange={(e) => setF({ ...f, province: e.target.value })}>
                <option value="">— escolher —</option>
                {PROVINCES.map((p) => <option key={p}>{p}</option>)}
              </Select>
            </Field>
            <Field label="Cidade ou distrito"><Input value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} /></Field>
            <div className="sm:col-span-2"><Field label="Morada" hint="Rua, número, bairro"><Input value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} /></Field></div>
          </Section>
          <Section title="Talão" description="O logótipo e a mensagem do fim do talão.">
            <div>
              <span className="text-sm font-medium">Logótipo</span>
              <div className="mt-1.5 flex items-center gap-3">
                <button type="button" onClick={() => file.current?.click()} aria-label={f.logo ? 'Trocar o logótipo' : 'Juntar logótipo'}
                  className="grid h-24 w-24 place-items-center overflow-hidden rounded-xl border-2 border-dashed border-base bg-white hover:border-brand-500">
                  {f.logo ? <img src={f.logo} alt="" className="max-h-full max-w-full object-contain" /> : <ImagePlus size={24} className="text-muted" />}
                </button>
                {f.logo && <Button variant="subtle" size="sm" icon={Trash2} onClick={() => setF({ ...f, logo: null })}>Tirar</Button>}
              </div>
              <input ref={file} type="file" accept="image/*" hidden onChange={async (e) => {
                const x = e.target.files?.[0]; e.target.value = '';
                if (!x) return;
                try { setF({ ...f, logo: await logoThumb(x) }); setErr(null); } catch (ex) { setErr((ex as Error).message); }
              }} />
              <span className="mt-1 block text-xs text-muted">De preferência a preto sobre fundo branco</span>
            </div>
            <Field label="Mensagem no fim do talão" hint={`${(f.receiptFooter ?? '').length}/300`}>
              <Textarea maxLength={300} rows={3} value={f.receiptFooter ?? ''} onChange={(e) => setF({ ...f, receiptFooter: e.target.value })} placeholder="Obrigado pela preferência!" />
            </Field>
          </Section>
        </div>
        {err && <div className="mt-6"><Alert>{err}</Alert></div>}
        <FormActions>
          <Button onClick={save} disabled={busy || !dirty}>{busy ? 'A gravar…' : 'Gravar'}</Button>
          {dirty && <Button variant="ghost" onClick={() => setF({ ...data.company })}>Desfazer</Button>}
        </FormActions>
      </Card>
    </div>
  );
}

const AREAS: ModuleArea[] = ['Retalho', 'Farmácia', 'Restauração e bar', 'Beleza e bem-estar', 'Distribuição', 'Moda', 'Serviços'];
const PLAN_NAME = { caixa: 'Caixa', loja: 'Loja', rede: 'Rede' } as const;

/** Definições → Módulos: o tipo de negócio e o que está ligado (só o dono muda). */
export function ModulesPage() {
  const biz = useBusiness();
  const { data, error, reload } = useLoad<CompanyData>('/settings/company');
  const me = useLoad<{ role: string }>('/auth/me');
  const [err, setErr] = useState<string | null>(null);
  const [changing, setChanging] = useState(false);
  const [choice, setChoice] = useState<BusinessChoice>(emptyChoice);
  const toast = useToast();
  if (error) return <Alert>{error}</Alert>;
  if (!data || !me.data) return <Card><Skeleton rows={8} /></Card>;
  const owner = me.data.role === 'owner';
  const type = businessType(data.business.type);
  const Icon = TYPE_ICON[type.code]!;
  const overrides = data.business.overrides;
  const enabled = resolveModules(type.code, overrides, 'trial');

  const set = async (body: unknown, msg: string) => {
    const e = await attempt(() => patch('/settings/business', body));
    setErr(e);
    if (!e) { toast(msg); await reload(); await biz.reload(); }
  };

  return (
    <div className="max-w-5xl space-y-6">
      <Card>
        <div className="flex flex-wrap items-center gap-4">
          <span className="grid h-14 w-14 place-items-center rounded-2xl bg-brand-700 text-white"><Icon size={28} /></span>
          <div className="min-w-0 flex-1">
            <div className="text-xs font-medium uppercase tracking-wide text-muted">Tipo de negócio · {FAMILIES.find((x) => x.key === type.family)?.name}</div>
            <div className="font-display text-xl font-semibold">{type.name}</div>
            <div className="text-sm text-muted">{type.description}</div>
          </div>
          {owner && <Button variant="ghost" onClick={() => { setChoice(emptyChoice()); setChanging(true); }}>Mudar o tipo</Button>}
        </div>
      </Card>
      {!owner && <Alert tone="info">Só o dono da empresa liga e desliga módulos.</Alert>}
      {err && <Alert>{err}</Alert>}
      {AREAS.map((area) => {
        const mods = MODULES.filter((m) => m.area === area);
        const mine = mods.some((m) => type.modules.includes(m.key) || enabled.includes(m.key));
        return (
          <details key={area} open={mine} className="surface group rounded-2xl shadow-xs">
            <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-4 py-3.5 xl:px-6">
              <span className="font-display text-lg font-semibold">{area}</span>
              <span className="flex items-center gap-2 text-sm text-muted">{mods.filter((m) => enabled.includes(m.key) && !m.soon).length} de {mods.length} ligados <ChevronRight size={18} className="transition group-open:rotate-90" /></span>
            </summary>
            <div className="grid gap-2 border-t border-base p-4 sm:grid-cols-2 xl:p-6">
              {mods.map((m) => {
                const on = enabled.includes(m.key) && !m.soon;
                const needed = MODULES.filter((x) => enabled.includes(x.key) && x.requires?.includes(m.key));
                const plan = !planAllows(data.business.plan, m.key);
                return (
                  <Toggle key={m.key} label={m.name} checked={on} disabled={!owner || m.soon}
                    description={<>{m.description}{needed.length > 0 && on && <span className="mt-1 block">Necessário para: {needed.map((x) => x.name).join(', ')}</span>}</>}
                    badge={m.soon ? <Badge tone="info">Em breve</Badge> : plan ? <Badge tone="warn">Plano {PLAN_NAME[m.minPlan]}</Badge> : type.modules.includes(m.key) ? <Badge>Do tipo</Badge> : undefined}
                    onChange={(v) => void set({ modules: { ...overrides, [m.key]: v } }, `${m.name} ${v ? 'ligado' : 'desligado'}`)} />
                );
              })}
            </div>
          </details>
        );
      })}
      {changing && (
        <Dialog title="Mudar o tipo de negócio" size="lg" onClose={() => setChanging(false)} footer={<>
          <Button variant="ghost" onClick={() => setChanging(false)}>Cancelar</Button>
          <Button disabled={!choice.type} onClick={async () => { await set(choicePayload(choice), 'Tipo de negócio mudado'); setChanging(false); }}>Mudar para este tipo</Button>
        </>}>
          <Alert tone="warn">Os artigos, as vendas e os clientes ficam todos. Mudam os módulos que vêm ligados e o ecrã onde a caixa abre.</Alert>
          <div className="mt-5"><BusinessPicker value={choice} onChange={setChoice} /></div>
        </Dialog>
      )}
    </div>
  );
}

/** Empresa criada pelo Makila: falta escolher o tipo de negócio (registo, passo 3). */
export function BusinessSetup() {
  const biz = useBusiness();
  const [choice, setChoice] = useState<BusinessChoice>(emptyChoice);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  return (
    <div className="mx-auto max-w-[640px]">
      <Card title="Que tipo de negócio é este?" description="A empresa veio do Makila Facturação. Falta dizer o tipo de negócio, para o POS se preparar.">
        <BusinessPicker value={choice} onChange={setChoice} />
        {err && <div className="mt-4"><Alert>{err}</Alert></div>}
        <FormActions>
          <Button disabled={!choice.type || busy} onClick={async () => {
            setBusy(true);
            const e = await attempt(() => patch('/settings/business', choicePayload(choice)));
            setBusy(false); setErr(e);
            if (!e) await biz.reload();
          }}>Continuar</Button>
        </FormActions>
      </Card>
    </div>
  );
}

interface Checklist { products: number; operators: number; terminals: number; sales: number; company: boolean; hours: boolean; fiscal: boolean }

/** "Primeiros passos" no Painel, conforme o tipo; desaparece quando está tudo feito. */
export function GettingStarted() {
  const { type } = useBusiness();
  const [c, setC] = useState<Checklist | null>(null);
  const [hidden, setHidden] = useState(() => { try { return localStorage.getItem('makila-pos-checklist-hidden') === '1'; } catch { return false; } });
  useEffect(() => { void api<Checklist>('/settings/checklist').then(setC, () => setC(null)); }, []);
  if (!c || hidden) return null;
  const steps = [
    { done: c.products > 0, label: `Rever os ${type.terms.items.toLowerCase()} e os preços`, path: '/gestao/artigos' },
    { done: c.operators > 0, label: `Criar os ${type.terms.staffPlural.toLowerCase()} com o PIN`, path: '/gestao/operadores' },
    { done: c.company, label: 'Completar os dados da empresa (telefone, logótipo, talão)', path: '/gestao/empresa' },
    { done: c.terminals > 0, label: 'Activar a primeira caixa', path: '/gestao/lojas' },
    { done: c.sales > 0, label: 'Fazer uma venda de teste', path: '/' },
    { done: c.fiscal, label: 'Activar a emissão fiscal (quando tiver treinado)', path: '/gestao/lojas' },
  ];
  const done = steps.filter((s) => s.done).length;
  if (done === steps.length) return null;
  return (
    <section aria-label="Primeiros passos" className="surface mb-6 rounded-2xl p-4 shadow-xs xl:p-6">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <span className="grid h-10 w-10 place-items-center rounded-xl bg-brand-50 text-brand-700 dark:bg-white/10"><ListChecks size={20} /></span>
        <div className="min-w-0 flex-1">
          <h2 className="font-display text-lg font-semibold">Primeiros passos</h2>
          <p className="text-sm text-muted">{done} de {steps.length} feitos</p>
        </div>
        <Button variant="subtle" size="sm" onClick={() => { setHidden(true); try { localStorage.setItem('makila-pos-checklist-hidden', '1'); } catch { /* fica só nesta sessão */ } }}>Esconder</Button>
      </div>
      <div className="mb-4 h-1.5 overflow-hidden rounded-full bg-surface-2"><div className="h-full rounded-full bg-brand-700 transition-all" style={{ width: `${(done / steps.length) * 100}%` }} /></div>
      <ol className="grid gap-1 sm:grid-cols-2">
        {steps.map((s, i) => (
          <li key={s.label}>
            <a href={s.path} onClick={(e) => { if (s.path.startsWith('/gestao')) { e.preventDefault(); navigate(s.path); } }}
              className={clsx('flex min-h-11 items-center gap-3 rounded-lg px-2 text-sm hover:bg-surface-2', s.done && 'text-muted')}>
              <span className={clsx('grid h-6 w-6 shrink-0 place-items-center rounded-full text-xs font-bold', s.done ? 'bg-green-600 text-white' : 'border-2 border-base')}>{s.done ? <Check size={14} /> : i + 1}</span>
              <span className={s.done ? 'line-through' : 'font-medium'}>{s.label}</span>
            </a>
          </li>
        ))}
      </ol>
    </section>
  );
}


